import { CrossStateEnum } from 'features/authorization/types';
import { IAuthorizationWithCrosses, ICrossWithServiceInfo } from '../types';

/** Normaliza e-tickets para comparación en deep links (?highlightEticket=). */
export const normalizeEticket = (value: string): string => value.trim().replace(/\s+/g, '');

export const eticketsMatch = (a: string, b: string): boolean => normalizeEticket(a) === normalizeEticket(b);

export const authorizationHasEticket = (authorization: IAuthorizationWithCrosses, eticket: string): boolean =>
	authorization.crossesData?.some((c) => eticketsMatch(c.eticket, eticket)) ?? false;

export const findAuthorizationIdForEticket = (
	authorizations: IAuthorizationWithCrosses[],
	eticket: string,
): number | undefined => authorizations.find((a) => authorizationHasEticket(a, eticket))?.id;

export type TicketCrossStatus = 'crossed' | 'uncrossed';

export interface AuthorizationTicketGroup {
	eticket: string;
	validatingCarrierName: string;
	coinCode: string;
	operator: string;
	/** Monto actualmente cruzado en este boleto (0 si descruzado). */
	netAmount: number;
	status: TicketCrossStatus;
	/** Fila de cruce activa para desvincular, si aplica. */
	activeCross: ICrossWithServiceInfo | null;
	/** Movimientos del ledger, ordenados cronológicamente. */
	history: ICrossWithServiceInfo[];
	lastActivityAt: string;
}

const findLastPositiveCross = (events: ICrossWithServiceInfo[]): ICrossWithServiceInfo | null => {
	for (let i = events.length - 1; i >= 0; i -= 1) {
		const event = events[i];
		if (event.amount > 0 && event.state === CrossStateEnum.CROSS) {
			return event;
		}
	}
	for (let i = events.length - 1; i >= 0; i -= 1) {
		if (events[i].amount > 0) return events[i];
	}
	return null;
};

export const groupCrossesByEticket = (crosses: ICrossWithServiceInfo[]): AuthorizationTicketGroup[] => {
	const byTicket = new Map<string, ICrossWithServiceInfo[]>();

	for (const cross of crosses) {
		const list = byTicket.get(cross.eticket) ?? [];
		list.push(cross);
		byTicket.set(cross.eticket, list);
	}

	const groups: AuthorizationTicketGroup[] = [];

	for (const [eticket, events] of byTicket) {
		const history = [...events].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
		const netAmount = Math.max(
			0,
			history.reduce((sum, e) => sum + e.amount, 0),
		);
		const status: TicketCrossStatus = netAmount > 0 ? 'crossed' : 'uncrossed';
		const activeCross = status === 'crossed' ? findLastPositiveCross(history) : null;
		const last = history[history.length - 1];

		groups.push({
			eticket,
			validatingCarrierName: last.validatingCarrierName,
			coinCode: last.coinCode,
			operator: activeCross?.operator ?? last.operator,
			netAmount,
			status,
			activeCross,
			history,
			lastActivityAt: last.createdAt,
		});
	}

	return groups.sort((a, b) => {
		if (a.status !== b.status) {
			return a.status === 'crossed' ? -1 : 1;
		}
		return new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime();
	});
};
